<template>
    <div class="global-address-administration">
        <global-modal v-show="show" @close="$emit('close')">
            <template slot="header">
                <h2 class="">{{ $t("titles." + showView) }}</h2>
            </template>
            <template slot="body">
                <global-address-administration-list v-if="showListView"
                                                    @delete="changeView(views.DELETE_VIEW, $event.detail)"
                                                    @edit="changeView(views.EDIT_VIEW, $event.detail)"
                                                    @select="selectAddress($event.detail)"
                                                    :start="currentPage * 6"
                                                    :end = "currentPage * 6 + 6"
                                                    :addresses="addressesSorted" />
                <global-address-administration-edit v-if="showEditView"
                                                    :errors.sync="errors"
                                                    :affected-address.sync="affectedAddress" />
                <global-address-administration-delete v-if="showDeleteView"
                                                      v-bind="Object.assign($props, $data)" :addresses="addressesSorted" />
            </template>
            <template slot="pagination" v-if="showView === views.LIST_VIEW">
                <global-button theme="secondary" icon="arrow-left" :disabled="currentPage <= 0" @click="currentPage--"></global-button>
                <global-button theme="secondary" icon="arrow-right" :disabled="currentPage >= pages" @click="currentPage++"></global-button>
            </template>
            <template v-if="showEditView">
                <global-button slot="controls__left" theme="secondary" @click="editCancel">{{ $t('buttons.cancel') }}</global-button>
                <global-button slot="controls__right" theme="primary" @click="editConfirm">{{ $t('buttons.save') }}</global-button>
            </template>
            <template v-if="showListView">
                <global-button slot="controls__left" theme="secondary" @click="newAddress">{{ $t('buttons.new') }}</global-button>
            </template>
            <template v-if="showDeleteView">
                <global-button slot="controls__left" theme="secondary" @click="deleteCancel">{{ $t('buttons.cancel') }}</global-button>
                <global-button slot="controls__right" theme="primary" @click="deleteConfirm">{{ $t('buttons.delete') }}</global-button>
            </template>
        </global-modal>
    </div>
</template>

<script>
import GlobalAddressAdministrationEdit
    from "@global/global-elements/global-address-administration/views/global-address-administration-edit";
import GlobalModal from "@global/global-elements/global-modal/global-modal";
import GlobalAddressAdministrationList
    from "@global/global-elements/global-address-administration/views/global-address-administration-list";
import GlobalButton from "@global/global-elements/global-button/global-button";
import GlobalAddressAdministrationDelete
    from "@global/global-elements/global-address-administration/views/global-address-administration-delete";
import {isInEu} from "@global/utilities/global-country-data";

const VIEWS = {
    LIST_VIEW: "list",
    EDIT_VIEW: "edit",
    NEW_VIEW: "new",
    DELETE_VIEW: "delete"
}

export default {
    name: "global-address-administration",
    components: {
        GlobalAddressAdministrationDelete,
        GlobalButton, GlobalAddressAdministrationEdit, GlobalAddressAdministrationList, GlobalModal},
    props: {
        addresses: Array,
        show: Boolean,
    },
    watch: {
        addresses(newValue) {
            this.addressData = newValue
        },
        show(newValue) {
            if(newValue) {
                if (this.addressData.length === 1) {
                    this.newAddress();
                }
            }
        }
    },
    data() {
        return {
            currentPage: 0,
            addressData: [],
            errors: {},
            showView: VIEWS.LIST_VIEW,
            affectedAddress: undefined,
        }
    },
    computed: {
        views() {
            return VIEWS
        },
        pages() {
            if (this.addressData) {
                return Math.ceil(this.addressData.length / 6) - 1
            }

            return 1
        },
        mainAddress() {
            return this.addressData.filter(address => address.mainAddress)[0]
        },
        showListView() {
            return this.showView === VIEWS.LIST_VIEW
        },
        showEditView() {
            return this.showView === VIEWS.EDIT_VIEW || this.showView === VIEWS.NEW_VIEW
        },
        showDeleteView() {
            return this.showView === VIEWS.DELETE_VIEW
        },
        addressesSorted() {
            const sorted = this.addressData.sort((a, b) =>  {
                if (a.lastUsed) {
                    return -1
                }
                if (b.lastUsed) {
                    return 1
                }
                if (a.company === b.company) {
                    return a.postCode < b.postCode ? -1 : 1
                } else {
                    return a.company < b.company ? -1 : 1
                }
            });

            sorted.forEach((el, index) => el.index = index);

            return sorted;
        }
    },
    methods: {
        changeView(view, addressKey = null) {
            this.showView = view;
            if (addressKey !== null) {
                this.affectedAddress = Object.assign({}, this.addressesSorted[addressKey])
            }
        },
        deleteCancel() {
            this.affectedAddress = undefined
            this.changeView(VIEWS.LIST_VIEW)
        },
        deleteConfirm() {
            this.addressData.splice(this.affectedAddress.index, 1)
            this.affectedAddress = undefined
            this.changeView(VIEWS.LIST_VIEW)
        },
        selectAddress(key) {
            this.closeAndReset(this.addressesSorted[key]);
        },
        newAddress() {
            this.affectedAddress = {
                company: "",
                firstName: "",
                lastName: "",
                street: "",
                postCode: "",
                city: "",
                country: "",
                phone: "",
                lastUsed: false,
                mainAddress: false,
                id: Math.round(Math.random() * 10000),
                index: -1
            };

            this.changeView(VIEWS.NEW_VIEW)
        },
        validateAddress(address) {
            this.errors = {};
            const addressKeys = Object.keys(address);
            const errorObject = {}
            addressKeys.forEach(key => {
                if (key !== 'company' && key !== 'phone') {
                    if (address[key] === '') {
                        errorObject[key] = this.$t("errors.addressFields." + key);
                    }
                }

                if (key === 'country') {
                    if(isInEu(this.mainAddress.country) && !isInEu(address[key])) {
                        errorObject[key] = this.$t("errors.addressFields." + key + "NotInEu")
                    }
                }

                if (key === 'phone' && address.phone !== '') {
                    const phoneValidation = new RegExp('^(\\+[1-9])?[0-9A-Z/()\\- ]+$');
                    if (!phoneValidation.test(address.phone)) {
                        errorObject[key] = this.$t("errors.addressFields." + key)
                    }
                }
            })

            if (Object.keys(errorObject).length) {
                this.errors = Object.assign( {}, this.errors, { addressFields: errorObject}, { general: { validationFailed: this.$t('errors.general.validationFailed')} })
            }

            return !Object.keys(this.errors).length;
        },
        editConfirm() {
            if(this.validateAddress(this.affectedAddress)) {
                if (this.affectedAddress.index === -1) {
                    this.addressData.push(this.affectedAddress)
                } else {
                    this.addressData.splice(this.affectedAddress.index, 1, this.affectedAddress)
                }

                this.closeAndReset(this.affectedAddress)
            }
        },
        editCancel() {
            this.errors = {}
            this.affectedAddress = undefined

            if (this.addressData.length === 1) {
                this.closeAndReset()
            }

            this.changeView(VIEWS.LIST_VIEW)
        },
        closeAndReset(address = undefined) {
            if (address) {
                this.$emit('saveChanges', { detail: this.addressesSorted.filter(el => el.mainAddress === false)})
                this.$emit('close', { detail: address })
            } else {
                this.$emit('close')
            }

            this.currentPage = 0;
            this.affectedAddress = undefined
            this.changeView(VIEWS.LIST_VIEW)
        }
    },
    mounted() {
        this.addressData = this.addresses
    }
}
</script>
